/**
 * Shopper-built comparison page. Product-context fragments are loaded through
 * the Section Rendering API so metafield formatting stays in Liquid.
 *
 * Handles come from ?ids= when the link names them (the compare tray button).
 * A plain Compare link, such as the one in the mega menu, has no ids, so the
 * comparison saved by product-compare.js is used instead.
 */
(() => {
  const TAG = 'product-compare-page';
  const MAX_PRODUCTS = 3;
  const DATA_SECTION = 'product-compare-data';
  const STORAGE_KEY = 'meaco-product-compare';
  const STORAGE_VERSION = 1;

  // Featured products section the compare template uses for its pick-more row.
  const PICK_MORE = '.section-featured-products';

  if (customElements.get(TAG)) return;

  // Same record product-compare.js writes. sections/main-compare.liquid reads
  // it before paint so the empty state does not flash.
  const storedCompareHandles = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');

      if (
        parsed?.version !== STORAGE_VERSION ||
        !Array.isArray(parsed.items) ||
        Number(parsed.expiresAt) <= Date.now()
      ) {
        return [];
      }

      const handles = [];

      for (const item of parsed.items) {
        const handle = typeof item?.handle === 'string' ? item.handle.trim() : '';

        if (!String(item?.productId || '') || !handle || handles.includes(handle)) continue;

        handles.push(handle);

        if (handles.length === MAX_PRODUCTS) break;
      }

      return handles;
    } catch {
      return [];
    }
  };

  class ProductComparePage extends HTMLElement {
    #products = [];
    #showDifferences = false;
    #emptyListTracked = false;

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      this.addEventListener('change', this.#onChange);
      this.querySelector('[data-compare-table]')?.addEventListener('scroll', this.#syncScrollDots);
      window.addEventListener('resize', this.#syncScrollDots);
      this.#load();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('change', this.#onChange);
      this.querySelector('[data-compare-table]')?.removeEventListener('scroll', this.#syncScrollDots);
      window.removeEventListener('resize', this.#syncScrollDots);
    }

    async #load() {
      const params = new URLSearchParams(window.location.search);
      let requested = (params.get('ids') || '')
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean);

      if (requested.length === 0) {
        requested = storedCompareHandles();

        if (requested.length > 0) {
          this.#updateUrl(requested);
          document.body.toggleAttribute('data-compare-loading', true);
          document.body.toggleAttribute('data-compare-table-active', true);
        }
      }

      const handles = [...new Set(requested)].slice(0, MAX_PRODUCTS);

      if (requested.length > MAX_PRODUCTS) {
        this.querySelector('[data-compare-max-message]')?.removeAttribute('hidden');
        window.ProductCompareStore?.track('compare_limit');
      }

      if (handles.length === 0) {
        this.#render();
        return;
      }

      this.setAttribute('aria-busy', 'true');

      // Claim the table state before the fetches resolve so the pick-more
      // carousel never flashes behind a comparison that is on its way.
      document.body.toggleAttribute('data-compare-table-active', true);

      const results = await Promise.all(handles.map((handle) => this.#fetchProduct(handle)));
      this.#products = results
        .filter((result) => result.status === 'loaded')
        .map((result) => result.product);
      this.removeAttribute('aria-busy');

      const hasTransientFailure = results.some((result) => result.status === 'error');

      if (hasTransientFailure) {
        this.querySelector('[data-compare-load-error]')?.removeAttribute('hidden');
      }

      if (!hasTransientFailure) {
        const retainedHandles = this.#syncStore(results);

        if (results.some((result) => result.status === 'unavailable')) {
          this.#updateUrl(retainedHandles);
        }
      }

      window.ProductCompareStore?.track('compare_view');
      this.#render();
    }

    #syncStore(results) {
      const nextItems = [];
      const retainedHandles = [];

      for (const result of results) {
        if (result.status === 'unavailable') continue;

        retainedHandles.push(result.handle);

        if (result.status === 'loaded') nextItems.push(result.product.item);
      }

      window.ProductCompareStore?.replace(nextItems);
      return retainedHandles;
    }

    async #fetchProduct(handle) {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(
          `${root}products/${encodeURIComponent(handle)}`,
          window.location.origin
        );

        url.searchParams.set('section_id', DATA_SECTION);

        const response = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: AbortSignal.timeout(10000),
        });

        if (!response.ok) return { status: 'error', handle };

        const html = await response.text();
        const documentRoot = new DOMParser().parseFromString(html, 'text/html');
        const template = documentRoot.querySelector('[data-compare-product-data]');
        const unavailable = documentRoot.querySelector('[data-compare-product-unavailable]');

        if (!(template instanceof HTMLTemplateElement)) {
          return unavailable
            ? {
                status: 'unavailable',
                handle: unavailable.getAttribute('data-product-handle') || handle,
              }
            : { status: 'error', handle };
        }

        const fragment = template.content.cloneNode(true);
        const header = fragment.querySelector('[data-compare-page-product]');
        const title = template.dataset.productTitle || '';
        const productId = template.dataset.productId || '';
        const productUrl = template.dataset.productUrl || '';
        const image = header?.querySelector('.compare-page__image');
        const price = header?.querySelector('.compare-page__product-price');
        const values = new Map();

        for (const wrapper of fragment.querySelectorAll('[data-compare-value]')) {
          const cell = wrapper.querySelector('.product-comparison__cell');

          if (cell) values.set(wrapper.getAttribute('data-compare-value'), cell);
        }

        return {
          status: 'loaded',
          handle: template.dataset.productHandle || handle,
          product: {
            header,
            values,
            item: {
              productId,
              handle: template.dataset.productHandle || handle,
              variantId: null,
              title,
              url: productUrl,
              image: image?.getAttribute('src') || '',
              price: price?.textContent?.trim() || '',
              priceAmount: null,
              available: true,
            },
          },
        };
      } catch {
        return { status: 'error', handle };
      }
    }

    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) return;

      const dot = target.closest('[data-compare-dot]');

      if (dot) {
        this.#scrollToDot(Number(dot.dataset.compareDot));
        return;
      }

      const remove = target.closest('[data-compare-page-remove]');
      const productLink = target.closest('.compare-page__product-link');

      if (productLink) {
        const productRoot = productLink.closest('[data-compare-page-product]');
        const entry = this.#products.find(
          (product) =>
            String(product.item.productId) === String(productRoot?.getAttribute('data-product-id'))
        );

        if (entry) this.#trackStandard('select_item', [entry.item]);
      }

      if (!remove) return;

      const productId = remove.getAttribute('data-product-id');
      this.#products = this.#products.filter(
        (entry) => String(entry.item.productId) !== String(productId)
      );
      window.ProductCompareStore?.remove(productId);
      this.#updateUrl();
      this.#render();
    };

    #onChange = (event) => {
      const input = event.target;

      if (!(input instanceof HTMLInputElement) || !input.matches('[data-compare-page-toggle]')) {
        return;
      }

      this.#showDifferences = input.checked;
      input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
      this.#syncRows();
    };

    #updateUrl(nextHandles = this.#products.map((entry) => entry.item.handle)) {
      const url = new URL(window.location.href);

      if (nextHandles.length > 0) {
        url.searchParams.set('ids', nextHandles.join(','));
      } else {
        url.searchParams.delete('ids');
      }

      window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    }

    #emptyColumn() {
      const template = this.querySelector('[data-compare-empty-column-template]');

      return template instanceof HTMLTemplateElement
        ? template.content.cloneNode(true)
        : document.createDocumentFragment();
    }

    #render() {
      const table = this.querySelector('[data-compare-table]');
      const semanticTable = this.querySelector('[role="table"]');
      const heading = this.querySelector('[data-compare-heading]');
      const headers = this.querySelector('[data-compare-headers]');
      const dots = this.querySelector('[data-compare-scroll-dots]');
      const count = this.#products.length;
      const hasTable = count > 0;

      this.dataset.compareCount = String(count);
      window.clearTimeout(window.themeComparePageLoadingFallback);
      delete window.themeComparePageLoadingFallback;
      document.body.removeAttribute('data-compare-loading');
      document.body.toggleAttribute('data-compare-table-active', hasTable);
      table?.toggleAttribute('hidden', !hasTable);

      if (heading) {
        heading.textContent = hasTable
          ? this.dataset.heading || ''
          : this.dataset.emptyHeading || '';
      }

      if (!hasTable || !headers) {
        dots?.toggleAttribute('hidden', true);
        this.#trackEmptyList();
        document.dispatchEvent(new CustomEvent('product-compare:change', { detail: {} }));
        return;
      }

      const visibleColumns = Math.min(MAX_PRODUCTS, count + (count < MAX_PRODUCTS ? 1 : 0));
      const semanticColumns = visibleColumns + 1;

      this.style.setProperty('--compare-page-columns', String(visibleColumns));
      semanticTable?.setAttribute('aria-colcount', String(semanticColumns));

      for (const groupCell of this.querySelectorAll('[data-compare-group-cell]')) {
        groupCell.setAttribute('aria-colspan', String(semanticColumns));
      }

      headers.replaceChildren();

      for (const entry of this.#products) {
        if (entry.header) headers.append(entry.header);
      }

      if (count < MAX_PRODUCTS) headers.append(this.#emptyColumn());

      for (const row of this.querySelectorAll('[data-compare-row]')) {
        const cells = row.querySelector('[data-compare-row-cells]');
        const reads = row.getAttribute('data-reads') || '';

        if (!cells) continue;
        cells.replaceChildren();

        for (const entry of this.#products) {
          const cell = valueFor(entry, reads)?.cloneNode(true);

          if (cell instanceof HTMLElement) {
            cell.setAttribute('role', 'cell');
            cells.append(cell);
          }
        }

        if (count < MAX_PRODUCTS) {
          const emptyCell = document.createElement('div');
          emptyCell.className = 'product-comparison__cell compare-page__empty-data-cell';
          emptyCell.setAttribute('role', 'cell');
          cells.append(emptyCell);
        }
      }

      this.#syncRows();
      requestAnimationFrame(this.#syncScrollDots);
      document.dispatchEvent(new CustomEvent('product-compare:change', { detail: {} }));
    }

    #syncScrollDots = () => {
      const scroller = this.querySelector('[data-compare-table]');
      const dots = this.querySelector('[data-compare-scroll-dots]');

      if (!scroller || !dots) return;

      const pages = this.#scrollPages(scroller);

      if (pages.length < 2) {
        delete dots.dataset.pages;
        dots.replaceChildren();
        dots.hidden = true;
        return;
      }

      const pageKey = pages.join(',');

      if (dots.dataset.pages !== pageKey) {
        const labelTemplate = dots.dataset.dotLabel || '';

        dots.dataset.pages = pageKey;
        dots.replaceChildren(
          ...pages.map((left, index) => {
            const dot = document.createElement('button');

            dot.type = 'button';
            dot.className = 'compare-page__scroll-dot';
            dot.dataset.compareDot = String(index);
            dot.dataset.compareLeft = String(left);
            dot.setAttribute('aria-label', labelTemplate.replace('__n__', String(index + 1)));
            return dot;
          })
        );
      }

      dots.hidden = false;

      let activeIndex = 0;

      for (const [index, left] of pages.entries()) {
        if (left <= scroller.scrollLeft + 1) activeIndex = index;
      }

      for (const [index, dot] of Array.from(dots.children).entries()) {
        const isActive = index === activeIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      }
    };

    #columns() {
      return this.querySelectorAll('[data-compare-headers] > *');
    }

    // One entry per scroll position that can park a column beside the sticky
    // label. Later columns that clamp to the same end position are dropped,
    // so a dot is never a duplicate of the one before it.
    #scrollPages(scroller) {
      const labelWidth = this.querySelector('.compare-page__toggle-cell')?.getBoundingClientRect().width ?? 0;
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const pages = [];

      for (const column of this.#columns()) {
        const left = Math.round(
          Math.min(
            maxScroll,
            Math.max(
              0,
              scroller.scrollLeft +
                (column.getBoundingClientRect().left - scroller.getBoundingClientRect().left) -
                labelWidth
            )
          )
        );
        const previous = pages[pages.length - 1];

        if (previous === undefined || left - previous > 1) pages.push(left);
      }

      return pages;
    }

    #scrollToDot(index) {
      const scroller = this.querySelector('[data-compare-table]');
      const dot = this.querySelectorAll('[data-compare-dot]')[index];
      const left = Number(dot?.dataset.compareLeft);

      if (!scroller || !dot || Number.isNaN(left)) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      scroller.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    #trackEmptyList() {
      if (this.#emptyListTracked) return;

      const cards = Array.from(document.querySelectorAll(`${PICK_MORE} [data-product-card]`));
      const listItems = cards.map((card, index) => ({
        productId: card.getAttribute('data-product-id') || '',
        title: card.querySelector('[data-product-card-title]')?.textContent?.trim() || '',
        priceAmount: Number(card.getAttribute('data-product-price')) / 100 || null,
        index,
      }));

      if (listItems.length === 0) return;

      this.#emptyListTracked = true;
      this.#trackStandard('view_item_list', listItems);
    }

    #trackStandard(event, eventItems) {
      const publish = window.Shopify?.analytics?.publish;

      if (typeof publish !== 'function') return;

      publish(`meaco:compare_${event}`, {
        ecommerce: {
          item_list_name: 'Compare',
          items: eventItems.map((item, index) => {
            const entry = {
              item_id: String(item.productId),
              item_name: item.title,
              index: item.index ?? index,
            };
            const price = Number(item.priceAmount);

            if (price) entry.price = price;

            return entry;
          }),
        },
      });
    }

    #syncRows() {
      let lastVisibleGroup = null;

      for (const row of this.querySelectorAll('[data-compare-row]')) {
        const productCells = Array.from(
          row.querySelectorAll('.product-comparison__cell:not(.compare-page__empty-data-cell)')
        );
        const values = productCells.map((cell) => cell.getAttribute('data-compare') || '');
        const allEmpty = values.length === 0 || values.every((value) => value === '');
        const allSame = values.length > 1 && values.every((value) => value === values[0]);
        const hidden = allEmpty || (this.#showDifferences && allSame);
        const group = row.getAttribute('data-group') || '';
        const heading = row.querySelector('[data-compare-group]');
        const headingRow = row.querySelector('[data-compare-group-row]');

        row.toggleAttribute('hidden', hidden);

        if (!heading) continue;

        // Each row block carries its group heading, so only the first visible
        // row of a group shows one.
        headingRow?.toggleAttribute('hidden', hidden || group === lastVisibleGroup);

        if (!hidden) lastVisibleGroup = group;
      }
    }
  }

  const normaliseReads = (reads) => reads.trim().replace(/^custom\./, '');

  const valueFor = (entry, reads) =>
    entry.values.get(reads) || entry.values.get(normaliseReads(reads));

  customElements.define(TAG, ProductComparePage);
})();
